import { useEffect, useState } from "react";
import {
  Accessibility, Gem, UserCheck, Wrench, DoorOpen, Heart, Award, ShieldCheck,
  Lightbulb, Globe2, KeyRound, Palette, BookOpen,
} from "lucide-react";
import { api } from "@/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";

const PILLARS = [
  { icon: Accessibility, title: "Accessibility", text: "Make high-quality education accessible irrespective of geographic location." },
  { icon: Gem, title: "Quality", text: "Develop structured programmes supported by knowledgeable educators." },
  { icon: UserCheck, title: "Personalisation", text: "Recognise that every learner has different goals and learning speeds." },
  { icon: Wrench, title: "Technology", text: "Use technology to enhance — not replace — the relationship between educators and learners." },
  { icon: DoorOpen, title: "Opportunity", text: "Help learners develop capabilities that create academic, professional and personal opportunities." },
];

const VALUES = [
  { icon: Heart, title: "Learner First", text: "Every product and decision should ultimately improve the learner experience." },
  { icon: Award, title: "Excellence", text: "Maintain high standards in teaching, curriculum, technology and operations." },
  { icon: ShieldCheck, title: "Integrity", text: "Operate transparently and responsibly with learners, educators, employees and partners." },
  { icon: Lightbulb, title: "Innovation", text: "Continuously explore better ways of teaching and learning." },
  { icon: Globe2, title: "Inclusivity", text: "Make meaningful learning opportunities available across geographical and socioeconomic boundaries." },
  { icon: KeyRound, title: "Ownership", text: "Encourage accountability and entrepreneurial thinking throughout the organisation." },
  { icon: Palette, title: "Creativity", text: "Recognise creative thinking as an essential component of both education and business." },
  { icon: BookOpen, title: "Continuous Learning", text: "Remain an organisation that learns while helping others learn." },
];

export default function Purpose() {
  const [company, setCompany] = useState(null);
  useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="purpose-page">
      <PageHero
        eyebrow="Why We Exist"
        title="Our purpose is to transform how India learns."
        lede="Roy Career Solutions Private Limited exists to help transform education by creating specialised learning organisations that combine the accessibility of digital technology with the effectiveness of structured, human-led teaching."
      />

      <section className="py-24 lg:py-32" data-testid="purpose-pillars">
        <div className="container-rcs">
          <Section eyebrow="Our Purpose" title="Five commitments behind everything we build." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="card-premium h-full" data-testid={`purpose-pillar-${p.title.toLowerCase()}`}>
                  <p.icon className="h-6 w-6 text-gold-dark" aria-hidden />
                  <h3 className="mt-5 font-serif text-xl text-navy-900">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-navy-900/10 bg-white py-24 lg:py-32" data-testid="purpose-vision-mission">
        <div className="container-rcs grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl bg-navy-950 p-10 lg:p-14" data-testid="purpose-vision">
              <p className="eyebrow-light">Our Vision</p>
              <p className="mt-6 font-serif text-2xl leading-snug text-ivory sm:text-3xl">
                “{company?.vision || "To build a diverse ecosystem of trusted education brands that empower learners to realise their academic, creative and professional potential."}”
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="h-full rounded-2xl border border-navy-900/10 bg-ivory p-10 lg:p-14" data-testid="purpose-mission">
              <p className="eyebrow">Our Mission</p>
              <p className="mt-6 text-base leading-relaxed text-slate-700 sm:text-lg">
                {company?.mission ||
                  "To develop learner-centric educational platforms that combine structured curricula, excellent educators, digital accessibility, innovation and personalised learning experiences."}
              </p>
              <div className="mt-10 grid grid-cols-2 gap-3">
                {["Quality Education", "Digital Accessibility", "Learner Success", "Continuous Innovation"].map((m) => (
                  <div key={m} className="rounded-xl border border-gold/40 bg-white px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-navy-900">
                    {m}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="purpose-values">
        <div className="container-rcs">
          <Section eyebrow="Core Values" title="The values that govern how we work." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="card-premium group h-full" data-testid={`purpose-value-${v.title.toLowerCase().replace(/ /g, "-")}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy-950 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-navy-950">
                    <v.icon className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className="mt-5 font-serif text-lg text-navy-900">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
